import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Check, CheckCircle2, Loader2, Mail, Bookmark, Smartphone, Monitor, Share, MoreVertical, Star, Link2, MousePointerClick } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import MobileShell from "../components/MobileShell";
import { MillieAvatar } from "../components/MascotBubble";
import { platform } from "../lib/storeApps";

const DEVICES = [["iphone", "iPhone", Smartphone], ["android", "Android", Smartphone], ["desktop", "Computer", Monitor]];

const STEPS = {
  iphone: [
    [Copy, "Copy the helper link", "Tap the Copy button below. That's Millie's helper — it only runs when you tap it."],
    [Share, "Bookmark Woolworths in Safari", "Open woolworths.com.au (or coles.com.au) in Safari. Tap Share → Add Bookmark, name it Millie, tap Save."],
    [Link2, "Paste the link into that bookmark", "Tap the Bookmarks (book) icon → Edit → tap Millie → replace the address with what you copied → Done."],
    [MousePointerClick, "Tap Millie when you shop", "Logged in at Woolworths? Open bookmarks, tap Millie. Your list appears and lands in the trolley."],
  ],
  android: [
    [Copy, "Copy the helper link", "Tap the Copy button below. That's Millie's helper — it only runs when you tap it."],
    [Star, "Bookmark Woolworths in Chrome", "Open woolworths.com.au (or coles.com.au) in Chrome. Tap ⋮ → ☆ to bookmark it."],
    [MoreVertical, "Edit the bookmark", "Tap ⋮ → Bookmarks → ⋮ next to the new one → Edit. Name it Millie and paste the link as the URL."],
    [MousePointerClick, "Type Millie in the address bar", "On any Woolworths page, tap the address bar, type Millie and pick the bookmark. Your list appears."],
  ],
  desktop: [
    [Bookmark, "Show your bookmarks bar", "Chrome / Edge: Ctrl+Shift+B (⌘+Shift+B on Mac). Safari: View → Show Favourites Bar."],
    [MousePointerClick, "Drag the Millie button", "Drag the pink Millie button below onto that bar."],
    [Star, "Open Woolworths and log in", "Go to woolworths.com.au or coles.com.au and sign in."],
    [MousePointerClick, "Click Millie", "Click the Millie bookmark. Your list appears on the right and fills your trolley."],
  ],
};

export default function HelperSetup() {
  const navigate = useNavigate();
  const [device, setDevice] = useState(platform());
  const [setup, setSetup] = useState(null);
  const [copied, setCopied] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connected, setConnected] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const timer = useRef(null);

  useEffect(() => { api.get("/trolley/setup").then(({ data }) => { setSetup(data); setConnected(data.helper_connected_at || null); }).catch(() => {}); }, []);
  useEffect(() => () => clearInterval(timer.current), []);

  const copy = async () => {
    try { await navigator.clipboard.writeText(setup.bookmarklet); setCopied(true); toast.success("Helper link copied"); } catch { toast.error("Couldn't copy — long-press the link box instead"); }
  };
  const test = () => {
    setTesting(true);
    const started = Date.now();
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get("/trolley/setup");
        if (data.helper_connected_at && new Date(data.helper_connected_at).getTime() > started - 5000) {
          setConnected(data.helper_connected_at); setTesting(false); clearInterval(timer.current);
          localStorage.setItem("sorted_helper_installed", "1");
          toast.success("Millie's helper is connected!");
        }
      } catch { /* keep waiting */ }
      if (Date.now() - started > 180000) { setTesting(false); clearInterval(timer.current); }
    }, 3000);
  };
  const email = async () => {
    setEmailing(true);
    try { const { data } = await api.post("/trolley/email-steps", { device }); toast.success(`Steps sent to ${data.to}`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setEmailing(false); }
  };

  return (
    <MobileShell>
      <div className="px-6 pt-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="helper-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft"><ArrowLeft size={19} strokeWidth={2.5} /></button>
        <h1 className="font-hand text-4xl text-sorted-ink">Millie's 1-tap helper</h1>
      </div>

      <div className="px-6 mt-4 flex items-start gap-3">
        <MillieAvatar size={48} float={false} mood="point" />
        <div className="bg-white rounded-3xl rounded-tl-md p-4 border-2 border-[#FDECEF] shadow-soft flex-1">
          <p className="text-sm font-semibold text-sorted-ink">Set this up once and I'll match every item and fill your Woolworths trolley while you're logged in — one tap, no typing.</p>
          <p className="text-[11px] text-sorted-slate font-semibold mt-1">Prefer not to? The <b>Send to Woolworths app</b> button on your list works with no setup at all.</p>
        </div>
      </div>

      <div className="px-6 mt-5" data-testid="helper-status">
        {connected ? (
          <div className="bg-[#DDEBE1] rounded-3xl p-4 flex items-center gap-3" data-testid="helper-status-connected">
            <CheckCircle2 size={26} strokeWidth={2.5} className="text-emerald-700" />
            <div>
              <p className="font-heading font-semibold text-sorted-ink leading-tight">Connected — you're all set</p>
              <p className="text-[11px] font-semibold text-emerald-800">Last used {new Date(connected).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-4 border-2 border-dashed border-[#FDECEF] flex items-center gap-3" data-testid="helper-status-waiting">
            <span className="w-7 h-7 rounded-full border-2 border-sorted-blush" />
            <p className="text-sm font-semibold text-sorted-slate">Not connected yet — follow the steps, then tap <b>Test it</b>.</p>
          </div>
        )}
      </div>

      <div className="px-6 mt-5">
        <p className="text-xs font-bold text-sorted-slate mb-2">I'm on</p>
        <div className="bg-white rounded-full p-1 flex border-2 border-[#FDECEF]" data-testid="helper-device">
          {DEVICES.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setDevice(k)} data-testid={`helper-device-${k}`}
              className={`flex-1 py-2 rounded-full text-xs font-bold flex items-center justify-center gap-1 transition-colors ${device === k ? "bg-sorted-ink text-white" : "text-sorted-slate"}`}><Icon size={13} /> {l}</button>
          ))}
        </div>
      </div>

      <div className="px-6 mt-5 space-y-3" data-testid="helper-steps">
        {STEPS[device].map(([Icon, title, body], i) => (
          <div key={title} className="bg-white rounded-3xl p-4 border-2 border-[#FDECEF] shadow-soft flex gap-3 animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${i * 80}ms` }} data-testid={`helper-step-${i + 1}`}>
            <span className="w-11 h-11 rounded-2xl bg-sorted-peach/60 text-sorted-ink flex items-center justify-center shrink-0"><Icon size={19} strokeWidth={2.5} /></span>
            <div className="flex-1">
              <p className="font-heading font-semibold text-sorted-ink leading-tight"><span className="text-sorted-rose mr-1">{i + 1}.</span>{title}</p>
              <p className="text-xs text-sorted-slate font-semibold mt-1">{body}</p>
              {i === 0 && device !== "desktop" && setup && (
                <div className="mt-3">
                  <button onClick={copy} data-testid="helper-copy-link" className="w-full bg-sorted-rose text-white font-bold py-3 rounded-full flex items-center justify-center gap-2">{copied ? <Check size={16} strokeWidth={3} /> : <Copy size={16} strokeWidth={2.5} />} {copied ? "Copied!" : "Copy helper link"}</button>
                  <textarea readOnly value={setup.bookmarklet} onFocus={(e) => e.target.select()} data-testid="helper-link-box" rows={2}
                    className="mt-2 w-full text-[10px] font-mono bg-muted rounded-xl p-2 text-sorted-slate outline-none resize-none" />
                </div>
              )}
              {i === 1 && device === "desktop" && setup && (
                <a href={setup.bookmarklet} onClick={(e) => e.preventDefault()} draggable data-testid="helper-drag-button"
                  className="mt-3 inline-flex items-center gap-1 bg-sorted-rose text-white px-5 py-2.5 rounded-full font-bold cursor-grab active:cursor-grabbing"><Bookmark size={15} strokeWidth={2.5} /> Millie</a>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="px-6 mt-5 grid grid-cols-2 gap-2">
        <button onClick={test} disabled={testing} data-testid="helper-test" className="bg-sorted-ink text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-70">
          {testing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} strokeWidth={2.5} />} {testing ? "Waiting for a tap…" : "Test it"}
        </button>
        <button onClick={email} disabled={emailing} data-testid="helper-email" className="bg-white border-2 border-[#FDECEF] text-sorted-ink font-bold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-70">
          {emailing ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} strokeWidth={2.5} />} Email me the steps
        </button>
      </div>
      {testing && <p className="px-6 mt-2 text-[11px] text-center text-sorted-slate font-semibold" data-testid="helper-testing-hint">Now open Woolworths or Coles and tap your Millie bookmark — this turns green the moment it connects.</p>}
    </MobileShell>
  );
}
